import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Divider,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  DragIndicator,
  Home as HomeIcon,
  StarBorder,
  Preview as PreviewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Fade,
  ListItemButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveIcon,
  Star,
} from '@mui/icons-material';
import { getMaterialIconComponent, MATERIAL_ICON_KEYS } from 'app/components/MaterialIconMap';
import { getMuiIconComponent, toLabel } from 'app/utils/muiIconsList';
import { SidebarNavigationItem, SidebarNavigationGroup } from '../../types';

import Role from 'app/apiService/role/roleApiService';
import { useTheme } from '@mui/material';

function resolveIcon(iconName: string, sx?: object): React.ReactNode {
  const el = getMuiIconComponent(iconName, sx);
  if (el) return el;
  const fallback = getMaterialIconComponent(iconName, sx);
  return fallback ?? null;
}

function isSidebarNavigationGroup(x: SidebarNavigationItem | SidebarNavigationGroup): x is SidebarNavigationGroup {
  return 'items' in x && Array.isArray((x as SidebarNavigationGroup).items);
}

/** UI state for a group (has id for keys/DnD). Supports 3 levels. */
export interface GroupState {
  id: string;
  label: string;
  icon: string;
  items: (SidebarNavigationItem | GroupState)[];
}

export type RootNode = SidebarNavigationItem | GroupState;

function addIdsToGroups(
  nodes: (SidebarNavigationItem | SidebarNavigationGroup)[],
  prefix: string
): (SidebarNavigationItem | GroupState)[] {
  return nodes.map((node, i) => {
    if (isSidebarNavigationGroup(node)) {
      const g = node as SidebarNavigationGroup;
      return {
        id: `${prefix}-${i}-${Date.now()}`,
        label: g.label || 'Group',
        icon: g.icon || 'folder',
        items: addIdsToGroups(g.items, `${prefix}-${i}`),
      };
    }
    return node as SidebarNavigationItem;
  });
}

/** Parse initial API data into root nodes (3-level, with ids on groups). */
function parseInitialNodes(initial: (SidebarNavigationItem | SidebarNavigationGroup)[]): RootNode[] {
  if (!initial?.length) return [];
  if (!initial.some(isSidebarNavigationGroup)) return initial as SidebarNavigationItem[];
  return addIdsToGroups(initial, 'group') as RootNode[];
}

/** Unique key for sidebar item: same link + different label = different items (e.g. Home vs Sales Portal). */
function sidebarItemKey(item: { link: string; label: string }): string {
  return `${item.link}\u0001${item.label}`;
}

/** Collect all link values from tree (for defaultHome check). */
function collectUsedLinks(nodes: RootNode[]): Set<string> {
  const out = new Set<string>();
  for (const node of nodes) {
    if (isSidebarNavigationGroup(node as SidebarNavigationGroup)) {
      const g = node as GroupState;
      collectUsedLinks(g.items as RootNode[]).forEach((link) => out.add(link));
    } else {
      out.add((node as SidebarNavigationItem).link);
    }
  }
  return out;
}

/** Collect (link, label) keys from tree so available pool excludes exact matches only. */
function collectUsedKeys(nodes: RootNode[]): Set<string> {
  const out = new Set<string>();
  for (const node of nodes) {
    if (isSidebarNavigationGroup(node as SidebarNavigationGroup)) {
      const g = node as GroupState;
      collectUsedKeys(g.items as RootNode[]).forEach((k) => out.add(k));
    } else {
      out.add(sidebarItemKey(node as SidebarNavigationItem));
    }
  }
  return out;
}

interface SidebarManagementProps {
  existingItems: (SidebarNavigationItem | SidebarNavigationGroup)[];
  availableItems: SidebarNavigationItem[];
  onUpdate: (selectedItems: SidebarNavigationItem[] | (SidebarNavigationItem | SidebarNavigationGroup)[]) => void;
  oldLayout: boolean;
}

interface SortableItemProps {
  item: SidebarNavigationItem;
  isSelected: boolean;
  onToggle: () => void;
  isDefault?: boolean;
  onSetDefault?: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, isSelected, onToggle, isDefault, onSetDefault }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sidebarItemKey(item) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = getMaterialIconComponent(item.icon);

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 0.25,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: isSelected ? 'rgba(255, 165, 0, 0.2)' : isDefault ? 'rgba(0, 0, 0, 0.05)' : 'background.paper',
        border: isDefault ? '2px solid #000' : '1px solid transparent',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isSelected ? 'rgba(255, 165, 0, 0.3)' : 'action.hover',
        },
      }}
      onMouseDown={(e) => {
        // Only toggle selection if not clicking on drag handle
        const target = e.target as HTMLElement;
        const dragHandle = target.closest('[data-drag-handle]');
        if (!dragHandle) {
          // Don't prevent default on mousedown to allow drag to work
          console.log('Item mousedown, will toggle selection for:', item.link);
        }
      }}
      onClick={(e) => {
        // Only toggle selection if not clicking on drag handle
        const target = e.target as HTMLElement;
        const dragHandle = target.closest('[data-drag-handle]');
        if (!dragHandle) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Item clicked, toggling selection for:', item.link);
          onToggle();
        }
      }}
    >
      <Box
        data-drag-handle
        sx={{ 
          minWidth: 32, 
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1,
        }}
        {...listeners}
        {...attributes}
        onMouseDown={(e) => {
          // Stop propagation to prevent item selection when clicking drag handle
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Stop propagation to prevent item selection when clicking drag handle
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <DragIndicator fontSize="small" color="action" />
      </Box>
      {IconComponent && (
        <ListItemIcon sx={{ minWidth: 28 }}>
          {React.cloneElement(IconComponent, { sx: { fontSize: 18 } })}
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.label}
        secondary={isDefault ? 'Default Home' : item.link}
        primaryTypographyProps={{ 
          fontWeight: isSelected || isDefault ? 'bold' : 'normal',
          fontSize: '0.875rem',
        }}
        secondaryTypographyProps={{
          fontSize: '0.75rem',
        }}
        sx={{ my: 0 }}
      />
      {isDefault && (
        <HomeIcon sx={{ color: 'primary.main', ml: 1 }} fontSize="small" />
      )}
      {onSetDefault && (
        <Tooltip title="Make it default home">
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSetDefault();
            }}
            sx={{
              ml: 1,
              color: isDefault ? 'primary.main' : 'action.active',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <StarBorder fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
};

interface NestedGroupEditorProps {
  group: GroupState;
  depth: number;
  availablePool: SidebarNavigationItem[];
  iconOptionsList: { value: string; label: string }[];
  expandedGroupIds: Set<string>;
  setExpandedGroupIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  updateGroup: (id: string, updates: Partial<{ label: string; icon: string }>) => void;
  addItemToGroup: (parentId: string, item: SidebarNavigationItem) => void;
  addSubGroup: (parentId: string) => void;
  removeFromGroup: (parentId: string, childId: string) => void;
  moveInGroup: (parentId: string, fromIndex: number, toIndex: number) => void;
  resolveIcon: (iconName: string, sx?: object) => React.ReactNode;
  isGroup: (n: RootNode) => n is GroupState;
  defaultHome: string;
  setDefaultHome: (link: string) => void;
}

const NestedGroupEditor: React.FC<NestedGroupEditorProps> = ({
  group,
  depth,
  availablePool,
  iconOptionsList,
  expandedGroupIds,
  setExpandedGroupIds,
  updateGroup,
  addItemToGroup,
  addSubGroup,
  removeFromGroup,
  moveInGroup,
  resolveIcon,
  isGroup,
  defaultHome,
  setDefaultHome,
}) => {
  const canAddSubGroup = depth < 3;
  const isExpanded = expandedGroupIds.has(group.id);
  const toggleExpanded = () => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(group.id)) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
  };
  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => {}}
      sx={{ '&:before': { display: 'none' }, boxShadow: 1 }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleExpanded();
            }}
            sx={{ pointerEvents: 'auto' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{ alignItems: 'center' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Label"
            value={group.label}
            onChange={(e) => updateGroup(group.id, { label: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: 160 }}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            onClick={(e) => e.stopPropagation()}
            options={iconOptionsList}
            value={iconOptionsList.find((o) => o.value === group.icon) ?? null}
            onChange={(_, newValue) => updateGroup(group.id, { icon: newValue?.value ?? 'folder' })}
            getOptionLabel={(opt) => opt.label}
            filterSelectedOptions={false}
            renderOption={(props, opt) => (
              <li {...props} key={opt.value}>
                <ListItemIcon sx={{ minWidth: 36 }}>{resolveIcon(opt.value, { fontSize: 20 })}</ListItemIcon>
                <ListItemText primary={opt.label} secondary={opt.value} primaryTypographyProps={{ fontSize: '0.875rem' }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Icon"
                placeholder="Search icons…"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {iconOptionsList.find((o) => o.value === group.icon) ? (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          {resolveIcon(group.icon, { fontSize: 20 })}
                        </Box>
                      ) : null}
                      {params.InputProps?.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" fontWeight="600" color="text.secondary">
            Level {depth + 1} (items / nested groups)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 200, maxHeight: 260, overflow: 'auto' }}>
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight="600">Available options</Typography>
              </Box>
              <List dense sx={{ py: 0 }}>
                {availablePool.length === 0 ? (
                  <ListItem><Typography variant="caption" color="text.secondary" fontStyle="italic">None left</Typography></ListItem>
                ) : (
                  availablePool.map((item) => (
                    <ListItem
                      key={item.link}
                      secondaryAction={
                        <IconButton size="small" onClick={() => addItemToGroup(group.id, item)}><AddIcon fontSize="small" /></IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{resolveIcon(item.icon, { fontSize: 18 })}</ListItemIcon>
                      <ListItemText primary={item.label} secondary={item.link} primaryTypographyProps={{ fontSize: '0.8rem' }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 200, maxHeight: 260, overflow: 'auto' }}>
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight="600">Current</Typography>
                {canAddSubGroup && (
                  <Tooltip title="Add nested group">
                    <IconButton size="small" onClick={() => addSubGroup(group.id)} sx={{ ml: 0.5 }}><AddIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Box>
              <List dense sx={{ py: 0 }}>
                {group.items.length === 0 ? (
                  <ListItem><Typography variant="caption" color="text.secondary" fontStyle="italic">No items</Typography></ListItem>
                ) : (
                  group.items.map((child, i) => {
                    if (isGroup(child)) {
                      return (
                        <ListItem key={child.id} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          <NestedGroupEditor
                            group={child}
                            depth={depth + 1}
                            availablePool={availablePool}
                            iconOptionsList={iconOptionsList}
                            expandedGroupIds={expandedGroupIds}
                            setExpandedGroupIds={setExpandedGroupIds}
                            updateGroup={updateGroup}
                            addItemToGroup={addItemToGroup}
                            addSubGroup={addSubGroup}
                            removeFromGroup={removeFromGroup}
                            moveInGroup={moveInGroup}
                            resolveIcon={resolveIcon}
                            isGroup={isGroup}
                            defaultHome={defaultHome}
                            setDefaultHome={setDefaultHome}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                            <IconButton size="small" onClick={() => moveInGroup(group.id, i, i - 1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => moveInGroup(group.id, i, i + 1)} disabled={i === group.items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => removeFromGroup(group.id, child.id)} color="error"><RemoveIcon fontSize="small" /></IconButton>
                          </Box>
                        </ListItem>
                      );
                    }
                    const item = child as SidebarNavigationItem;
                    const isDefault = item.link === defaultHome;
                    return (
                      <ListItem
                        key={item.link}
                        secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={isDefault ? 'Default home' : 'Set as default home'}>
                              <IconButton size="small" onClick={() => setDefaultHome(item.link)} sx={{ color: isDefault ? 'primary.main' : 'action.active' }}>
                                {isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => moveInGroup(group.id, i, i - 1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => moveInGroup(group.id, i, i + 1)} disabled={i === group.items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => removeFromGroup(group.id, item.link)} color="error"><RemoveIcon fontSize="small" /></IconButton>
                          </Box>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>{resolveIcon(item.icon, { fontSize: 18 })}</ListItemIcon>
                        <ListItemText primary={item.label} secondary={isDefault ? 'Default home' : item.link} primaryTypographyProps={{ fontSize: '0.8rem' }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Paper>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

interface RootNodeRowProps {
  node: RootNode;
  index: number;
  rootNodesLength: number;
  getNodeId: (node: RootNode) => string;
  isGroup: (n: RootNode) => n is GroupState;
  resolveIcon: (iconName: string, sx?: object) => React.ReactNode;
  availablePool: SidebarNavigationItem[];
  iconOptionsList: { value: string; label: string }[];
  expandedGroupIds: Set<string>;
  setExpandedGroupIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  updateGroup: (id: string, updates: Partial<{ label: string; icon: string }>) => void;
  moveRootNode: (fromIndex: number, toIndex: number) => void;
  removeRootNode: (nodeId: string) => void;
  addItemToGroup: (parentId: string, item: SidebarNavigationItem) => void;
  addSubGroup: (parentId: string) => void;
  removeFromGroup: (parentId: string, childId: string) => void;
  moveInGroup: (parentId: string, fromIndex: number, toIndex: number) => void;
  defaultHome: string;
  setDefaultHome: (link: string) => void;
}

const RootNodeRow: React.FC<RootNodeRowProps> = ({
  node,
  index,
  rootNodesLength,
  getNodeId,
  isGroup,
  resolveIcon,
  availablePool,
  iconOptionsList,
  expandedGroupIds,
  setExpandedGroupIds,
  updateGroup,
  moveRootNode,
  removeRootNode,
  addItemToGroup,
  addSubGroup,
  removeFromGroup,
  moveInGroup,
  defaultHome,
  setDefaultHome,
}) => {
  const id = getNodeId(node);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  if (isGroup(node)) {
    return (
      <ListItem ref={setNodeRef} style={style} sx={{ display: 'block', p: 0, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', p: 0.5 }} {...listeners} {...attributes}>
            <DragIndicator fontSize="small" color="action" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <NestedGroupEditor
              group={node}
              depth={1}
              availablePool={availablePool}
              iconOptionsList={iconOptionsList}
              expandedGroupIds={expandedGroupIds}
              setExpandedGroupIds={setExpandedGroupIds}
              updateGroup={updateGroup}
              addItemToGroup={addItemToGroup}
              addSubGroup={addSubGroup}
              removeFromGroup={removeFromGroup}
              moveInGroup={moveInGroup}
              resolveIcon={resolveIcon}
              isGroup={isGroup}
              defaultHome={defaultHome}
              setDefaultHome={setDefaultHome}
            />
          </Box>
          <IconButton size="small" onClick={() => moveRootNode(index, index - 1)} disabled={index === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => moveRootNode(index, index + 1)} disabled={index === rootNodesLength - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => removeRootNode(id)} color="error"><RemoveIcon fontSize="small" /></IconButton>
        </Box>
      </ListItem>
    );
  }
  const item = node as SidebarNavigationItem;
  const isDefault = item.link === defaultHome;
  return (
    <ListItem ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'center', borderRadius: 1, border: '1px solid', borderColor: isDefault ? 'primary.main' : 'divider', mb: 0.5 }}>
      <Box sx={{ cursor: 'grab', mr: 1 }} {...listeners} {...attributes}><DragIndicator fontSize="small" color="action" /></Box>
      <ListItemIcon sx={{ minWidth: 32 }}>{resolveIcon(item.icon, { fontSize: 18 })}</ListItemIcon>
      <ListItemText primary={item.label} secondary={isDefault ? 'Default home' : item.link} primaryTypographyProps={{ fontSize: '0.875rem' }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
      <Tooltip title={isDefault ? 'Default home' : 'Set as default home'}>
        <IconButton size="small" onClick={() => setDefaultHome(item.link)} sx={{ color: isDefault ? 'primary.main' : 'action.active' }}>
          {isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
        </IconButton>
      </Tooltip>
      <IconButton size="small" onClick={() => moveRootNode(index, index - 1)} disabled={index === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={() => moveRootNode(index, index + 1)} disabled={index === rootNodesLength - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={() => removeRootNode(id)} color="error"><RemoveIcon fontSize="small" /></IconButton>
    </ListItem>
  );
};

const SidebarManagement: React.FC<SidebarManagementProps> = ({
  existingItems: initialExistingItems,
  availableItems: initialAvailableItems,
  onUpdate,
  oldLayout,
}) => {
  const initialNodesParsed = useMemo(
    () => (oldLayout ? parseInitialNodes((initialExistingItems || []) as (SidebarNavigationItem | SidebarNavigationGroup)[]) : []),
    [oldLayout, initialExistingItems]
  );
  const [rootNodes, setRootNodes] = useState<RootNode[]>(initialNodesParsed);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [defaultHome, setDefaultHome] = useState<string>('');

  function getNodeId(node: RootNode): string {
    return isSidebarNavigationGroup(node as SidebarNavigationGroup) ? (node as GroupState).id : `item-${(node as SidebarNavigationItem).link}`;
  }

  function serializeToPayload(nodes: RootNode[], defaultHomeLink?: string): (SidebarNavigationItem | SidebarNavigationGroup)[] {
    return nodes.map((node) => {
      if (isSidebarNavigationGroup(node as SidebarNavigationGroup)) {
        const g = node as GroupState;
        return { label: g.label, icon: g.icon, items: serializeToPayload(g.items as RootNode[], defaultHomeLink) };
      }
      const item = node as SidebarNavigationItem;
      return { ...item, isDefault: defaultHomeLink ? item.link === defaultHomeLink : (item.isDefault ?? false) };
    });
  }

  function getDefaultLinkFromTree(nodes: RootNode[]): string {
    for (const node of nodes) {
      if (isSidebarNavigationGroup(node as SidebarNavigationGroup)) {
        const found = getDefaultLinkFromTree((node as GroupState).items as RootNode[]);
        if (found) return found;
      } else {
        const item = node as SidebarNavigationItem;
        if (item.isDefault) return item.link;
      }
    }
    return '';
  }

  useEffect(() => {
    if (!oldLayout) return;
    onUpdate(serializeToPayload(rootNodes, defaultHome));
  }, [oldLayout, rootNodes, defaultHome]);

  // Sync defaultHome from initial nested data when old layout loads (only set if not already set)
  useEffect(() => {
    if (!oldLayout || !initialExistingItems?.length) return;
    const link = getDefaultLinkFromTree(parseInitialNodes(initialExistingItems as (SidebarNavigationItem | SidebarNavigationGroup)[]));
    if (link) setDefaultHome((prev) => prev || link);
  }, [oldLayout, initialExistingItems]);

  // Clear defaultHome when the default item is removed from the tree (old layout)
  useEffect(() => {
    if (!oldLayout || !defaultHome) return;
    const used = collectUsedLinks(rootNodes);
    if (!used.has(defaultHome)) setDefaultHome('');
  }, [oldLayout, defaultHome, rootNodes]);

  // Helper: remove duplicates by (link + label) so same link with different labels (e.g. Home vs Sales Portal) both stay
  const deduplicateItems = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    const seen = new Set<string>();
    return items.filter(item => {
      const key = sidebarItemKey(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Helper function to assign order to items based on their position
  const assignOrder = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    return items.map((item, index) => ({
      ...item,
      order: item.order !== undefined ? item.order : index + 1,
    }));
  };

  // Helper function to update order after reordering
  const updateOrder = (items: SidebarNavigationItem[]): SidebarNavigationItem[] => {
    return items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
  };

  /** Flatten old layout sidebar_navigation_mappings (up to 3 levels) to SidebarNavigationItem[] */
  const flattenSidebarNavigationMappings = (mappings: any[]): SidebarNavigationItem[] => {
    const flat: SidebarNavigationItem[] = [];
    function walk(nodes: any[]) {
      for (const node of nodes || []) {
        if (!node || typeof node !== 'object') continue;
        if (typeof node.link === 'string' && typeof node.label === 'string' && typeof node.icon === 'string') {
          flat.push({ label: node.label, link: node.link, icon: node.icon });
        } else if (Array.isArray(node.items)) {
          walk(node.items);
        }
      }
    }
    walk(mappings || []);
    return flat;
  };

  const [existingItems, setExistingItems] = useState<SidebarNavigationItem[]>(() => {
    if (oldLayout) return [];
    const raw = (initialExistingItems || []) as SidebarNavigationItem[];
    const items = deduplicateItems(raw);
    const sorted = items.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return 0;
    });
    return assignOrder(sorted);
  });
  const [availableItems, setAvailableItems] = useState<SidebarNavigationItem[]>(() => 
    deduplicateItems(initialAvailableItems || [])
  );
  const [selectedExisting, setSelectedExisting] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [existingSearch, setExistingSearch] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isOldLayoutUnavailable, setIsOldLayoutUnavailable] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: window.innerWidth / 2 - 300, y: 100 });
  const [previewHoveredGroupId, setPreviewHoveredGroupId] = useState<string | null>(null);
  const [previewSize, setPreviewSize] = useState({ width: 600, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  
  const theme = useTheme();
  const iconOptionsList = useMemo(
    () =>
      MATERIAL_ICON_KEYS.map((value) => ({ value, label: toLabel(value) })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only activate drag after 8px of movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const prevExistingItemsRef = useRef<string>('');
  const prevDefaultHomeRef = useRef<string>('');
  const onUpdateRef = useRef(onUpdate);

  // Keep onUpdate ref updated
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Find default home from existing items - only set if explicitly marked as default (flat layout only)
  useEffect(() => {
    if (oldLayout) return;
    if (existingItems.length === 0) {
      if (defaultHome) setDefaultHome('');
      return;
    }
    
    // Find item that explicitly has isDefault: true
    const defaultItem = existingItems.find(item => item.isDefault === true);
    
    if (defaultItem) {
      // If an item has isDefault: true, set defaultHome to that item's link
      if (defaultItem.link !== defaultHome) {
        setDefaultHome(defaultItem.link);
      }
    } else {
      // If no item has isDefault: true, only clear defaultHome if current defaultHome is not in the list
      // Don't automatically set to first item
      if (defaultHome && !existingItems.some(item => item.link === defaultHome)) {
        setDefaultHome('');
      }
    }
  }, [existingItems, oldLayout]);

  // Notify parent of changes only when values actually change (flat layout only)
  useEffect(() => {
    if (oldLayout) return;
    const existingItemsKey = JSON.stringify(
      existingItems.map(item => ({ link: item.link, label: item.label, icon: item.icon, order: item.order }))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    );
    const hasChanged = 
      prevExistingItemsRef.current !== existingItemsKey || 
      prevDefaultHomeRef.current !== defaultHome;

    if (hasChanged) {
      // Ensure items have order and isDefault set correctly
      // isDefault should ONLY be true if item.link matches defaultHome (user-selected default)
      // If defaultHome is not set, no item should have isDefault: true
      const itemsWithDefault = existingItems.map((item, index) => ({
        ...item,
        isDefault: defaultHome ? item.link === defaultHome : false,
        order: item.order !== undefined ? item.order : index + 1,
      }));
      
      console.log('SidebarManagement - Updating items with isDefault:', itemsWithDefault.map(item => ({
        label: item.label,
        isDefault: item.isDefault,
        link: item.link,
        defaultHome
      })));
      
      prevExistingItemsRef.current = existingItemsKey;
      prevDefaultHomeRef.current = defaultHome;
      
      // Use ref to avoid dependency on onUpdate
      onUpdateRef.current(itemsWithDefault);
    }
  }, [existingItems, defaultHome, oldLayout]);

  // Fetch available items from API - only once when component mounts or oldLayout changes
  useEffect(() => {
    const fetchAvailableItems = async () => {
      if (initialAvailableItems && initialAvailableItems.length > 0) {
        setAvailableItems(initialAvailableItems);
        return; // Use provided items
      }

      setLoading(true);
      setError('');
      setIsOldLayoutUnavailable(false);
      try {
        // Only send old_layout to get all available sidebar items (not role-specific)
        const params: any = { old_layout: oldLayout };

        console.log('Fetching sidebar navigation with params:', params);
        const response = await Role.getSidebarNavigation(params);
        console.log('Sidebar navigation API response (full):', JSON.stringify(response, null, 2));
        
        // Check for error response indicating old layout is not available
        const statusCode = response?.statusCode;
        const errorCode = response?.errorCode;
        const statusText = response?.statusText;
        const hasErrorStatus = 
          (statusCode !== undefined && (String(statusCode) === '500' || statusCode === 500)) ||
          (errorCode !== undefined && (String(errorCode) === '500' || Number(errorCode) === 500)) ||
          statusText === 'fail';
        
        if (hasErrorStatus && !oldLayout) {
          const errorMessage = String(response?.message || '');
          const isEmptyBody = !response?.body || 
            (typeof response.body === 'object' && Object.keys(response.body).length === 0);
          if (isEmptyBody && (errorMessage.includes('old layout') || errorMessage.includes('Role name is required'))) {
            setIsOldLayoutUnavailable(true);
            setAvailableItems([]);
            setError('');
            setLoading(false);
            return;
          }
        }
        
        // Try multiple response structures
        let items: any[] = [];
        
        if (response?.body) {
          const body = response.body;
          console.log('Response body type:', typeof body, 'Is array:', Array.isArray(body));
          
          if (Array.isArray(body)) {
            items = body;
          } else if (body && typeof body === 'object') {
            if (Object.keys(body).length === 0 && !oldLayout) {
              setIsOldLayoutUnavailable(true);
              setAvailableItems([]);
              setError('');
              setLoading(false);
              return;
            }
            if (Object.keys(body).length === 0 && oldLayout) {
              setAvailableItems([]);
              setError('');
              setLoading(false);
              return;
            }
            // Try nested structures (new layout) and old layout sidebar_navigation_mappings
            if (Array.isArray(body.sidebar_navigation_mappings) && body.sidebar_navigation_mappings.length > 0) {
              items = flattenSidebarNavigationMappings(body.sidebar_navigation_mappings);
            } else {
              items = body.items || body.data || body.sidebar_navigation || [];
            }
          }
        } else if (Array.isArray(response)) {
          // Response might be directly an array
          items = response;
        }
        
        console.log('Extracted items array:', items);
        console.log('Items count:', items.length);
        
        // Ensure each item has required fields
        const validItems = items.filter(item => {
          const isValid = item && 
            typeof item.label === 'string' && 
            typeof item.link === 'string' && 
            typeof item.icon === 'string';
          if (!isValid) {
            console.warn('Invalid item filtered out:', item);
          }
          return isValid;
        });
        
          console.log('Valid items after filtering:', validItems);
          console.log('Setting availableItems to:', validItems.length, 'items');
          
          // Remove duplicates and assign order before setting
          const itemsWithOrder = assignOrder(deduplicateItems(validItems));
          setAvailableItems(itemsWithOrder);
        
        if (validItems.length === 0) {
          if (items.length > 0) {
            setError(`All ${items.length} items were filtered out due to missing required fields`);
          } else {
            setError('No sidebar items found in response');
          }
        } else {
          setError(''); // Clear error if we got valid items
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sidebar items';
        setError(errorMessage);
        console.error('Error fetching sidebar navigation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableItems();
  }, [oldLayout]);

  // Filter out items that are already in existing items (match by both link and label)
  const availableItemsFiltered = useMemo(() => {
    const filtered = availableItems.filter(
      item => !existingItems.some(existing => existing.link === item.link && existing.label === item.label)
    );
    console.log('Available items:', availableItems.length);
    console.log('Existing items:', existingItems.length);
    console.log('Filtered available items:', filtered.length);
    return filtered;
  }, [availableItems, existingItems]);

  // Sort existing items by order before filtering
  const sortedExisting = useMemo(() => {
    return [...existingItems].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [existingItems]);

  const filteredExisting = sortedExisting.filter(item =>
    item.label.toLowerCase().includes(existingSearch.toLowerCase()) ||
    item.link.toLowerCase().includes(existingSearch.toLowerCase())
  );

  const filteredAvailable = availableItemsFiltered.filter(item =>
    item.label.toLowerCase().includes(availableSearch.toLowerCase()) ||
    item.link.toLowerCase().includes(availableSearch.toLowerCase())
  );

  const toggleSelection = (key: string, listType: 'existing' | 'available') => {
    if (listType === 'existing') {
      setSelectedExisting(prev => {
        const newSet = new Set(prev);
        if (newSet.has(key)) newSet.delete(key);
        else newSet.add(key);
        return newSet;
      });
    } else {
      setSelectedAvailable(prev => {
        const newSet = new Set(prev);
        if (newSet.has(key)) newSet.delete(key);
        else newSet.add(key);
        return newSet;
      });
    }
  };

  const moveSelected = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      const itemsToMove = availableItemsFiltered.filter(item => selectedAvailable.has(sidebarItemKey(item)));
      setAvailableItems(prev => prev.filter(item => !selectedAvailable.has(sidebarItemKey(item))));
      setExistingItems(prev => {
        const existingKeys = new Set(prev.map(item => sidebarItemKey(item)));
        const newItems = itemsToMove.filter(item => !existingKeys.has(sidebarItemKey(item)));
        const updated = deduplicateItems([...prev, ...newItems]);
        return updateOrder(updated);
      });
      setSelectedAvailable(new Set());
    } else {
      const itemsToMove = existingItems.filter(item => selectedExisting.has(sidebarItemKey(item)));
      setExistingItems(prev => {
        const filtered = prev.filter(item => !selectedExisting.has(sidebarItemKey(item)));
        return updateOrder(filtered);
      });
      setAvailableItems(prev => {
        const existingKeys = new Set(prev.map(item => sidebarItemKey(item)));
        const newItems = itemsToMove.filter(item => !existingKeys.has(sidebarItemKey(item)));
        return deduplicateItems([...prev, ...newItems]);
      });
      setSelectedExisting(new Set());
      
      // If default home was removed, reset it
      if (itemsToMove.some(item => item.link === defaultHome)) {
        const remaining = existingItems.filter(item => !selectedExisting.has(sidebarItemKey(item)));
        if (remaining.length > 0) {
          setDefaultHome(remaining[0].link);
        } else {
          setDefaultHome('');
        }
      }
    }
  };

  const moveAll = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      setExistingItems(prev => {
        const existingKeys = new Set(prev.map(item => sidebarItemKey(item)));
        const newItems = availableItemsFiltered.filter(item => !existingKeys.has(sidebarItemKey(item)));
        const updated = deduplicateItems([...prev, ...newItems]);
        return updateOrder(updated);
      });
      setAvailableItems(prev => prev.filter(item =>
        !availableItemsFiltered.some(filtered => sidebarItemKey(filtered) === sidebarItemKey(item))
      ));
      setSelectedAvailable(new Set());
    } else {
      setAvailableItems(prev => {
        const existingKeys = new Set(prev.map(item => sidebarItemKey(item)));
        const newItems = existingItems.filter(item => !existingKeys.has(sidebarItemKey(item)));
        return deduplicateItems([...prev, ...newItems]);
      });
      setExistingItems([]);
      setSelectedExisting(new Set());
      setDefaultHome('');
    }
  };

  // Handle setting default home - set clicked item as default
  // The useEffect will automatically update all items' isDefault based on defaultHome
  const handleSetDefault = useCallback((itemLink: string) => {
    setDefaultHome(itemLink);
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setExistingItems(items => {
        const oldIndex = items.findIndex(item => sidebarItemKey(item) === active.id);
        const newIndex = items.findIndex(item => sidebarItemKey(item) === over.id);
        if (oldIndex === -1 || newIndex === -1) return items;
        const reordered = arrayMove(items, oldIndex, newIndex);
        return updateOrder(reordered);
      });
    }
  };

  // Drag handlers for preview modal
  const handlePreviewDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!previewHeaderRef.current) return;
    const rect = previewHeaderRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep modal within viewport
      const maxX = window.innerWidth - previewSize.width;
      const maxY = window.innerHeight - 50;
      
      setPreviewPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, previewSize]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: previewSize.width,
      height: previewSize.height,
    });
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      const newWidth = Math.max(400, Math.min(resizeStart.width + deltaX, window.innerWidth - previewPosition.x));
      const newHeight = Math.max(300, Math.min(resizeStart.height + deltaY, window.innerHeight - previewPosition.y));
      
      setPreviewSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, previewPosition]);

  const availablePool = useMemo(() => {
    if (!oldLayout) return [];
    const usedKeys = collectUsedKeys(rootNodes);
    return availableItems.filter((a) => !usedKeys.has(sidebarItemKey(a)));
  }, [oldLayout, availableItems, rootNodes]);

  function isGroup(n: RootNode): n is GroupState {
    return typeof n === 'object' && n !== null && 'id' in n && 'items' in n;
  }

  function updateGroupInTree(nodes: RootNode[], groupId: string, updates: Partial<{ label: string; icon: string }>): RootNode[] {
    return nodes.map((n) => {
      if (isGroup(n) && n.id === groupId) return { ...n, ...updates };
      if (isGroup(n)) return { ...n, items: updateGroupInTree(n.items as RootNode[], groupId, updates) };
      return n;
    });
  }
  function addItemToGroupInTree(nodes: RootNode[], parentId: string, item: SidebarNavigationItem): RootNode[] {
    return nodes.map((n) => {
      if (isGroup(n) && n.id === parentId) return { ...n, items: [...n.items, item] };
      if (isGroup(n)) return { ...n, items: addItemToGroupInTree(n.items as RootNode[], parentId, item) };
      return n;
    });
  }
  function addSubGroupInTree(nodes: RootNode[], parentId: string): RootNode[] {
    const newGroup: GroupState = { id: `group-${Date.now()}`, label: 'New group', icon: 'folder', items: [] };
    return nodes.map((n) => {
      if (isGroup(n) && n.id === parentId) return { ...n, items: [...n.items, newGroup] };
      if (isGroup(n)) return { ...n, items: addSubGroupInTree(n.items as RootNode[], parentId) };
      return n;
    });
  }
  function removeFromGroupInTree(nodes: RootNode[], parentId: string, childId: string): RootNode[] {
    return nodes.map((n) => {
      if (isGroup(n) && n.id === parentId) {
        return { ...n, items: n.items.filter((c) => (isGroup(c) ? c.id !== childId : (c as SidebarNavigationItem).link !== childId)) };
      }
      if (isGroup(n)) return { ...n, items: removeFromGroupInTree(n.items as RootNode[], parentId, childId) };
      return n;
    });
  }
  function moveInGroupInTree(nodes: RootNode[], parentId: string, fromIndex: number, toIndex: number): RootNode[] {
    return nodes.map((n) => {
      if (isGroup(n) && n.id === parentId) {
        const items = arrayMove([...n.items], fromIndex, toIndex);
        return { ...n, items };
      }
      if (isGroup(n)) return { ...n, items: moveInGroupInTree(n.items as RootNode[], parentId, fromIndex, toIndex) };
      return n;
    });
  }

  const addRootItem = (item: SidebarNavigationItem) => setRootNodes((prev) => [...prev, item]);
  const addRootGroup = () =>
    setRootNodes((prev) => [...prev, { id: `group-${Date.now()}`, label: 'New group', icon: 'folder', items: [] }]);
  const updateGroup = (id: string, updates: Partial<{ label: string; icon: string }>) =>
    setRootNodes((prev) => updateGroupInTree(prev, id, updates));
  const moveRootNode = (fromIndex: number, toIndex: number) =>
    setRootNodes((prev) => arrayMove(prev, fromIndex, toIndex));
  const removeRootNode = (nodeId: string) =>
    setRootNodes((prev) => prev.filter((n) => getNodeId(n) !== nodeId));
  const addItemToGroup = (parentId: string, item: SidebarNavigationItem) =>
    setRootNodes((prev) => addItemToGroupInTree(prev, parentId, item));
  const addSubGroup = (parentId: string) => setRootNodes((prev) => addSubGroupInTree(prev, parentId));
  const removeFromGroup = (parentId: string, childId: string) =>
    setRootNodes((prev) => removeFromGroupInTree(prev, parentId, childId));
  const moveInGroup = (parentId: string, fromIndex: number, toIndex: number) =>
    setRootNodes((prev) => moveInGroupInTree(prev, parentId, fromIndex, toIndex));

  const rootNodeIds = useMemo(() => rootNodes.map((n) => getNodeId(n)), [rootNodes]);
  const handleRootDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = rootNodeIds.indexOf(active.id as string);
    const toIdx = rootNodeIds.indexOf(over.id as string);
    if (fromIdx === -1 || toIdx === -1) return;
    moveRootNode(fromIdx, toIdx);
  };

  return (
    <Box>
      {isOldLayoutUnavailable ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            p: 4,
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            This feature is not available right now for old layout
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {oldLayout ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">Sidebar (max 3 levels)</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Add direct item">
                    <IconButton color="primary" onClick={() => setExpandedGroupIds((prev) => { const next = new Set(prev); if (next.has('available-drawer')) next.delete('available-drawer'); else next.add('available-drawer'); return next; })} size="small" sx={{ border: 1, borderColor: 'divider' }}>
                      <AddIcon /> <Typography variant="caption" sx={{ ml: 0.5 }}>Item</Typography>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add group">
                    <IconButton color="primary" onClick={addRootGroup} size="small">
                      <AddIcon /> <Typography variant="caption" sx={{ ml: 0.5 }}>Group</Typography>
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Level 1: direct items or groups. Level 2–3: items or nested groups. Drag to reorder at root.
              </Typography>
              {expandedGroupIds.has('available-drawer') && (
                <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                  <Typography variant="caption" fontWeight="600" sx={{ display: 'block', mb: 1 }}>Add direct item (level 1)</Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {availablePool.length === 0 ? (
                      <ListItem><Typography variant="caption" color="text.secondary" fontStyle="italic">None left</Typography></ListItem>
                    ) : (
                      availablePool.map((item) => (
                        <ListItem
                          key={item.link}
                          secondaryAction={<IconButton size="small" onClick={() => { addRootItem(item); setExpandedGroupIds((prev) => { const next = new Set(prev); next.delete('available-drawer'); return next; }); }}><AddIcon fontSize="small" /></IconButton>}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>{resolveIcon(item.icon, { fontSize: 18 })}</ListItemIcon>
                          <ListItemText primary={item.label} secondary={item.link} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                        </ListItem>
                      ))
                    )}
                  </List>
                  <IconButton size="small" onClick={() => setExpandedGroupIds((prev) => { const next = new Set(prev); next.delete('available-drawer'); return next; })}><CloseIcon fontSize="small" /></IconButton>
                </Paper>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRootDragEnd}>
                <SortableContext items={rootNodeIds} strategy={verticalListSortingStrategy}>
                  <List dense sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {rootNodes.map((node, rIdx) => (
                      <RootNodeRow
                        key={getNodeId(node)}
                        node={node}
                        index={rIdx}
                        rootNodesLength={rootNodes.length}
                        getNodeId={getNodeId}
                        isGroup={isGroup}
                        resolveIcon={resolveIcon}
                        availablePool={availablePool}
                        iconOptionsList={iconOptionsList}
                        expandedGroupIds={expandedGroupIds}
                        setExpandedGroupIds={setExpandedGroupIds}
                        updateGroup={updateGroup}
                        moveRootNode={moveRootNode}
                        removeRootNode={removeRootNode}
                        addItemToGroup={addItemToGroup}
                        addSubGroup={addSubGroup}
                        removeFromGroup={removeFromGroup}
                        moveInGroup={moveInGroup}
                        defaultHome={defaultHome}
                        setDefaultHome={setDefaultHome}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
              {rootNodes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>Add an item or group above.</Typography>
              )}
            </Box>
          ) : (
          <>
          {/* Sidebar Management Header with Preview Icon */}
          {/* <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sidebar Management
            </Typography>
            <Tooltip title="Preview Sidebar">
              <IconButton
                size="small"
                onClick={() => setShowPreview(!showPreview)}
                sx={{
                  color: showPreview ? 'primary.main' : 'action.active',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <PreviewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box> */}

          {/* Sidebar Preview Modal - Portal to document.body */}
          {showPreview && (oldLayout ? rootNodes.length > 0 : existingItems.length > 0) && createPortal(
            <Box
              sx={{
                position: 'fixed',
                left: `${previewPosition.x}px`,
                top: `${previewPosition.y}px`,
                bgcolor: 'background.paper',
                boxShadow: theme.shadows[8],
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1300,
                overflow: 'visible',
                border: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              {/* Draggable Header */}
              <Box
                ref={previewHeaderRef}
                onMouseDown={handlePreviewDragStart}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.background.paper,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Sidebar Preview
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowPreview(false)}
                  sx={{ color: 'text.secondary', p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Preview Content - No padding, direct sidebar */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: oldLayout ? '72px' : '256px',
                    backgroundColor: oldLayout ? '#0F284C' : theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
                    borderRight: oldLayout ? 'none' : `1px solid ${theme.palette.divider}`,
                    borderRadius: oldLayout ? 0 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: oldLayout ? 'center' : 'flex-start',
                    pt: oldLayout ? 2 : 1,
                    pb: oldLayout ? 2 : 1,
                    boxShadow: theme.shadows[4],
                    border: oldLayout ? 'none' : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {(() => {
                    // Old layout: use groups (group icon + items with item icons). New layout: use existingItems.
                    const sortedNavigation = [...existingItems].map(item => ({
                      ...item,
                      isDefault: defaultHome ? item.link === defaultHome : (item.isDefault || false),
                    })).sort((a, b) => {
                      const orderA = a.order !== undefined ? a.order : 999;
                      const orderB = b.order !== undefined ? b.order : 999;
                      return orderA - orderB;
                    });

                    return oldLayout ? (
                      // Old Layout - Blue Theme: icon strip only; hover on group opens flyout to the right (like actual sidebar)
                      (() => {
                        const stripBg = '#0F284C';
                        return (
                          <Box sx={{ position: 'relative', display: 'flex', width: '100%' }}>
                            {/* Icon strip - only root-level icons */}
                            <Box
                              sx={{
                                width: 72,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: 2,
                                pb: 2,
                                gap: 0.5,
                              }}
                            >
                              {rootNodes.map((n, idx) => {
                                if (isGroup(n)) {
                                  return (
                                    <Tooltip key={n.id} title={n.label} placement="right" arrow>
                                      <Box
                                        onMouseEnter={() => setPreviewHoveredGroupId(n.id)}
                                        onMouseLeave={() => setPreviewHoveredGroupId(null)}
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: 2,
                                          cursor: 'pointer',
                                          backgroundColor: previewHoveredGroupId === n.id ? 'rgba(255,255,255,0.12)' : 'transparent',
                                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                        }}
                                      >
                                        {resolveIcon(n.icon, { fontSize: 22, color: '#fff' })}
                                      </Box>
                                    </Tooltip>
                                  );
                                }
                                const item = n as SidebarNavigationItem;
                                const isDefault = defaultHome ? item.link === defaultHome : false;
                                return (
                                  <Tooltip key={item.link ?? `item-${idx}`} title={item.label} placement="right" arrow>
                                    <Box
                                      sx={{
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        backgroundColor: isDefault ? 'rgba(255,255,255,0.15)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                      }}
                                    >
                                      {resolveIcon(item.icon, { fontSize: 22, color: '#fff' })}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                            {/* Flyout panel on hover - like actual Sidebar sub-panel */}
                            {rootNodes.filter(isGroup).map((grp) => (
                              <Fade
                                key={grp.id}
                                in={previewHoveredGroupId === grp.id}
                                timeout={180}
                                mountOnEnter
                                unmountOnExit
                              >
                                <Box
                                  onMouseEnter={() => setPreviewHoveredGroupId(grp.id)}
                                  onMouseLeave={() => setPreviewHoveredGroupId(null)}
                                  sx={{
                                    position: 'absolute',
                                    left: 72,
                                    top: 0,
                                    minWidth: 180,
                                    maxWidth: 260,
                                    background: stripBg,
                                    borderRadius: '0 16px 16px 0',
                                    boxShadow: theme.shadows[12],
                                    py: 2,
                                    px: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    color: '#fff',
                                    zIndex: 1,
                                  }}
                                >
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: '#fff' }}>
                                    {grp.label}
                                  </Typography>
                                  <Divider sx={{ mb: 0.5, borderColor: 'rgba(255,255,255,0.2)' }} />
                                  <List sx={{ p: 0 }}>
                                    {(grp.items as RootNode[]).map((sub) => {
                                      if (isGroup(sub)) {
                                        return (
                                          <Accordion
                                            key={sub.id}
                                            sx={{
                                              background: 'transparent',
                                              boxShadow: 'none',
                                              '&:before': { display: 'none' },
                                              '&.Mui-expanded': { margin: 0 },
                                            }}
                                          >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />} sx={{ minHeight: 40, py: 0, '& .MuiAccordionSummary-content': { margin: '4px 0' } }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>{resolveIcon(sub.icon, { fontSize: 20, color: '#fff' })}</ListItemIcon>
                                                <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.875rem' }}>{sub.label}</Typography>
                                              </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 0, pt: 0 }}>
                                              <List sx={{ p: 0 }}>
                                                {(sub.items as RootNode[]).map((leaf) => {
                                                  if (isGroup(leaf)) return null;
                                                  const leafItem = leaf as SidebarNavigationItem;
                                                  return (
                                                    <ListItemButton key={leafItem.link} sx={{ borderRadius: 2, color: '#fff', py: 0.75, px: 2, pl: 4, mb: 0.5, '&:hover': { background: 'rgba(255,255,255,0.08)' } }}>
                                                      <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>{resolveIcon(leafItem.icon, { fontSize: 18, color: '#fff' })}</ListItemIcon>
                                                      <ListItemText primary={leafItem.label} primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: '#fff' } }} />
                                                    </ListItemButton>
                                                  );
                                                })}
                                              </List>
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      }
                                      const subItem = sub as SidebarNavigationItem;
                                      return (
                                        <ListItemButton
                                          key={subItem.link}
                                          sx={{ borderRadius: 2, color: '#fff', py: 1, px: 2, mb: 0.5, '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
                                        >
                                          <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>{resolveIcon(subItem.icon, { fontSize: 20, color: '#fff' })}</ListItemIcon>
                                          <ListItemText primary={subItem.label} primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: '#fff' } }} />
                                        </ListItemButton>
                                      );
                                    })}
                                  </List>
                                </Box>
                              </Fade>
                            ))}
                          </Box>
                        );
                      })()
                    ) : (
                      // New Layout - Black/White Theme (Full sidebar with labels)
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.5, pt: 2 }}>
                        {sortedNavigation.map((item, index) => {
                          const IconComponent = getMaterialIconComponent(item.icon);
                          const isDefault = item.isDefault === true;
                          return (
                            <Box
                              key={item.link || index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 1.5,
                                py: 1.2,
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor: isDefault ? '#000000' : 'transparent',
                                color: isDefault
                                  ? '#ffffff'
                                  : theme.palette.text.primary,
                                '&:hover': {
                                  backgroundColor: isDefault
                                    ? '#1a1a1a'
                                    : theme.palette.action.hover,
                                },
                              }}
                            >
                              {IconComponent && React.cloneElement(IconComponent, {
                                sx: { fontSize: 20, color: isDefault ? '#ffffff' : 'inherit' }
                              })}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* Resize Handle */}
              <Box
                ref={resizeHandleRef}
                onMouseDown={handleResizeStart}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 20,
                  height: 20,
                  cursor: 'nwse-resize',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '0 0 12px 12px',
                    borderColor: `transparent transparent ${theme.palette.text.secondary} transparent`,
                  },
                }}
              />
            </Box>,
            document.body
          )}

          <Box display="flex" gap={4} p={2}>
        {/* Available Sidebar Items (Left) */}
        <Paper sx={{ width: '100%', flex: 1 }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                Available options
              </Typography>
              <Chip
                label={`${selectedAvailable.size} of ${availableItemsFiltered.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search available options..."
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <List sx={{ height: 400, overflow: 'auto', p: 0.5 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={24} />
              </Box>
            ) : filteredAvailable.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  fontStyle: 'italic',
                  gap: 1,
                }}
              >
                <Typography>No items available</Typography>
                {availableItems.length > 0 && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    ({availableItems.length} items from API, but all are already in sidebar)
                  </Typography>
                )}
                {error && (
                  <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                    Error: {error}
                  </Typography>
                )}
              </Box>
            ) : (
              filteredAvailable.map((item) => {
                const IconComponent = getMaterialIconComponent(item.icon);
                const key = sidebarItemKey(item);
                return (
                  <ListItem
                    key={key}
                    sx={{
                      mb: 0.25,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: selectedAvailable.has(key)
                        ? 'rgba(255, 165, 0, 0.2)'
                        : 'background.paper',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedAvailable.has(key)
                          ? 'rgba(255, 165, 0, 0.3)'
                          : 'action.hover',
                      },
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelection(key, 'available');
                    }}
                  >
                    {IconComponent && (
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {React.cloneElement(IconComponent, { sx: { fontSize: 18 } })}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      secondary={item.link}
                      primaryTypographyProps={{
                        fontWeight: selectedAvailable.has(key) ? 'bold' : 'normal',
                        fontSize: '0.875rem',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                      sx={{ my: 0 }}
                    />
                  </ListItem>
                );
              })
            )}
          </List>
        </Paper>

        {/* Transfer Controls */}
        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
          <Tooltip title="Move all to sidebar">
            <span>
              <IconButton
                onClick={() => moveAll('right')}
                disabled={availableItemsFiltered.length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardDoubleArrowRight fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move selected to sidebar">
            <span>
              <IconButton
                onClick={() => moveSelected('right')}
                disabled={selectedAvailable.size === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardArrowRight fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={`Move selected to available (${selectedExisting.size} selected)`}>
            <span>
              <IconButton
                onClick={() => {
                  console.log('Moving selected items left. Selected:', Array.from(selectedExisting));
                  moveSelected('left');
                }}
                disabled={selectedExisting.size === 0}
                size="large"
                color={selectedExisting.size > 0 ? "primary" : "default"}
                sx={{ 
                  border: '1px solid', 
                  borderColor: selectedExisting.size > 0 ? 'primary.main' : 'divider',
                  opacity: selectedExisting.size === 0 ? 0.4 : 1,
                  cursor: selectedExisting.size === 0 ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: selectedExisting.size > 0 ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                <KeyboardArrowLeft fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Move all to available">
            <span>
              <IconButton
                onClick={() => moveAll('left')}
                disabled={existingItems.length === 0}
                size="large"
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <KeyboardDoubleArrowLeft fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Current Sidebar Items (Right) */}
        <Paper sx={{ width: '100%', flex: 1 }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                Current options
              </Typography>
              <Chip
                label={`${selectedExisting.size} of ${existingItems.length} selected`}
                size="small"
                color={selectedExisting.size > 0 ? "primary" : "default"}
                variant={selectedExisting.size > 0 ? "filled" : "outlined"}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search current options..."
              value={existingSearch}
              onChange={(e) => setExistingSearch(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              variant="outlined"
            />
          </Box>
          <Divider />
          <Box sx={{ height: 400, overflow: 'auto', p: 0.5 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={24} />
              </Box>
            ) : filteredExisting.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  fontStyle: 'italic',
                }}
              >
                No items in sidebar
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredExisting.map(item => sidebarItemKey(item))}
                  strategy={verticalListSortingStrategy}
                >
                  <List>
                    {filteredExisting.map((item) => (
                      <SortableItem
                        key={sidebarItemKey(item)}
                        item={item}
                        isSelected={selectedExisting.has(sidebarItemKey(item))}
                        onToggle={() => toggleSelection(sidebarItemKey(item), 'existing')}
                        isDefault={item.link === defaultHome}
                        onSetDefault={() => handleSetDefault(item.link)}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </Paper>
      </Box>
        </>
          )}
        </>
      )}
    </Box>
  );
};

export default SidebarManagement;
